import type { ReactNode } from "react";
import { SkipLink } from "../core/SkipLink.js";
import { cn } from "../foundations/cn.js";
import { useLayout } from "../foundations/themeContext.js";

export interface PageShellProps {
  /** TopNav. */
  nav: ReactNode;
  /** MobileTopBar. */
  mobileTop?: ReactNode;
  /** MobileTabBar; leave it out on full-screen flows. */
  mobileTabs?: ReactNode;
  footer?: ReactNode;
  /** True when the page opens on a stage that runs under a floating nav (Home's hero). */
  underNav?: boolean;
  skipLabel?: string;
  children: ReactNode;
  className?: string;
}

/**
 * The frame of every page: the skip link, both navigations, the main landmark and the footer.
 * It leaves room under an attached or floating nav, unless the page's own stage runs beneath, and
 * sets `--bottom-bar-inset` to the height a phone's bottom bar takes, so a sticky element clears it.
 * A button a page floats above that bar (`data-floating-action`) sets `--floating-action-inset`,
 * which the footer adds to its own room at the foot.
 */
export function PageShell({
  nav,
  mobileTop,
  mobileTabs,
  footer,
  underNav = false,
  skipLabel,
  children,
  className,
}: PageShellProps) {
  const layout = useLayout();
  return (
    <div
      className={cn(
        "flex min-h-dvh flex-col max-md:has-[[data-floating-action]]:[--floating-action-inset:3.25rem]",
        mobileTabs &&
          (layout.header === "floating"
            ? "max-md:[--bottom-bar-inset:5.25rem]"
            : "max-md:[--bottom-bar-inset:4rem]"),
        className,
      )}
    >
      <SkipLink targetId="content">{skipLabel}</SkipLink>
      {nav}
      {mobileTop}
      <main
        id="content"
        tabIndex={-1}
        className={cn(
          "flex flex-1 flex-col outline-none",
          !underNav && "max-md:pt-20",
          !underNav && layout.header === "floating" && "md:pt-28",
        )}
      >
        {children}
      </main>
      {footer}
      {mobileTabs}
    </div>
  );
}
